'use client';

import { useEffect, useRef, useState } from 'react';
import type { StructuredBrief } from '@/lib/schemas';

interface ConfirmedAnswer { label: string; value: string }
interface ChatMessage { role: 'user' | 'assistant'; content: string }

interface ApiResponse {
  reply: string;
  suggestions: string[];
  newlyConfirmed: ConfirmedAnswer[];
  updates: Partial<StructuredBrief>;
  isComplete: boolean;
}

interface Props {
  initialBrief: StructuredBrief;
  onComplete: (finalBrief: StructuredBrief) => void;
  searchComplete?: boolean;
  onNewSearch?: () => void;
}

function mergeBrief(base: StructuredBrief, updates: Partial<StructuredBrief>): StructuredBrief {
  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(updates).filter(([, v]) => v !== null && v !== undefined)
    ),
    locations: updates.locations?.length ? updates.locations : base.locations,
    must_haves: updates.must_haves?.length ? updates.must_haves : base.must_haves,
    lifestyle_preferences: updates.lifestyle_preferences?.length ? updates.lifestyle_preferences : base.lifestyle_preferences,
    deal_breakers: updates.deal_breakers?.length ? updates.deal_breakers : base.deal_breakers,
  };
}

function confirmedFromBrief(brief: StructuredBrief): ConfirmedAnswer[] {
  const out: ConfirmedAnswer[] = [];
  if (brief.budget_min || brief.budget_max) {
    const min = brief.budget_min ? `£${brief.budget_min.toLocaleString()}` : '';
    const max = brief.budget_max ? `£${brief.budget_max.toLocaleString()}` : '';
    out.push({ label: 'Budget', value: min && max ? `${min}–${max}` : max || min });
  }
  if (brief.bedrooms_min)  out.push({ label: 'Bedrooms',      value: `${brief.bedrooms_min}+` });
  if (brief.property_type) out.push({ label: 'Property type', value: brief.property_type });
  const locs = Array.isArray(brief.locations) ? brief.locations : [];
  if (locs.length)         out.push({ label: 'Location',      value: locs.join(', ') });
  if (brief.commute_requirements) out.push({ label: 'Commute', value: brief.commute_requirements });
  if (brief.school_preferences)   out.push({ label: 'Schools', value: brief.school_preferences });
  return out;
}

export default function BriefQuiz({ initialBrief, onComplete, searchComplete, onNewSearch }: Props) {
  const [brief, setBrief]                   = useState<StructuredBrief>(initialBrief);
  const [messages, setMessages]             = useState<ChatMessage[]>([]);
  const [confirmed, setConfirmed]           = useState<ConfirmedAnswer[]>(() => confirmedFromBrief(initialBrief));
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [suggestions, setSuggestions]       = useState<string[]>([]);
  const [input, setInput]                   = useState('');
  const [isLoading, setIsLoading]           = useState(false);
  const [isDone, setIsDone]                 = useState(false);
  const initialized                         = useRef(false);
  const inputRef                            = useRef<HTMLInputElement>(null);

  // Kick off the quiz on mount
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    callApi([], '', brief);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function callApi(history: ChatMessage[], userMessage: string, currentBrief: StructuredBrief) {
    setIsLoading(true);
    try {
      const res = await fetch('/api/chat/filters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief: currentBrief, messages: history, userMessage }),
      });
      const data: ApiResponse = await res.json();

      const nextBrief = mergeBrief(currentBrief, data.updates ?? {});
      setBrief(nextBrief);

      if (data.newlyConfirmed?.length) {
        setConfirmed((prev) => {
          const existing = new Set(prev.map((c) => c.label));
          const fresh = data.newlyConfirmed.filter((c) => !existing.has(c.label));
          return [...prev, ...fresh];
        });
      }

      setCurrentQuestion(data.reply);
      setSuggestions(data.suggestions ?? []);

      const updatedHistory: ChatMessage[] = [
        ...history,
        ...(userMessage ? [{ role: 'user' as const, content: userMessage }] : []),
        { role: 'assistant' as const, content: data.reply },
      ];
      setMessages(updatedHistory);

      if (data.isComplete) {
        setIsDone(true);
        // Small delay so user sees the completion message before search starts
        setTimeout(() => onComplete(nextBrief), 1200);
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isLoading || isDone) return;
    setInput('');
    setSuggestions([]);
    callApi(messages, trimmed, brief);
  }

  return (
    <div className="flex w-full max-w-sm flex-col gap-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">

      {/* Header */}
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Smart Property Search</p>
          <p className="text-sm font-semibold text-slate-800 leading-tight">Let's refine your search</p>
        </div>
      </div>

      {/* Confirmed answers chips */}
      {confirmed.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-5 py-3">
          {confirmed.map((c) => (
            <span
              key={c.label}
              className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-semibold">{c.label}:</span>&nbsp;{c.value}
            </span>
          ))}
        </div>
      )}

      {/* Current question */}
      <div className="px-5 py-4">
        {isLoading && !currentQuestion ? (
          <div className="flex items-center gap-2 text-slate-400">
            <span className="flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </span>
          </div>
        ) : (
          <p className={`text-sm leading-relaxed ${isDone ? 'font-semibold text-emerald-700' : 'text-slate-700'}`}>
            {currentQuestion}
          </p>
        )}

        {/* Loading indicator while waiting for next question */}
        {isLoading && currentQuestion && (
          <div className="mt-2 flex items-center gap-1.5 text-slate-400">
            <span className="flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-blue-300 animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </span>
            <span className="text-[11px]">Thinking…</span>
          </div>
        )}
      </div>

      {/* Suggestion chips */}
      {suggestions.length > 0 && !isDone && (
        <div className="flex flex-wrap gap-1.5 px-5 pb-3">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => send(s)}
              disabled={isLoading}
              className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition hover:bg-blue-100 disabled:opacity-40"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input row */}
      {!isDone && (
        <div className="flex gap-2 border-t border-slate-100 px-5 py-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(input); }}
            placeholder="Type your answer…"
            disabled={isLoading}
            className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => send(input)}
            disabled={!input.trim() || isLoading}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white transition hover:bg-blue-700 disabled:opacity-40"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Search state */}
      {isDone && !searchComplete && (
        <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-3 text-sm text-slate-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
          Searching for properties…
        </div>
      )}
      {isDone && searchComplete && (
        <div className="border-t border-slate-100 px-5 py-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm text-emerald-600 font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Search complete
          </div>
          <button
            type="button"
            onClick={onNewSearch}
            className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-200 transition hover:bg-blue-700 active:scale-[0.98]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            New Search
          </button>
        </div>
      )}
    </div>
  );
}
